import { Injectable, type WritableSignal, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import type { DriveConversationPermissions, DriveEffectivePermissionEntry } from '@mtm/drive/core';

import { FeatureDetail } from '../constants/permission.constants';
import { EffectivePermissionEntry } from '../interfaces/auth-permissions.interface';
import { AllowedOperationsUpdatedEvent, UserLoggedOutEvent } from '../models/events';
import { AuthPermissionsService } from '../services/auth-permissions.service';
import { EmitterService } from '../services/emitter.service';

/**
 * Supplies the Drive library's audience manager with the caller's CORE_CONVERSATION rights,
 * which show or hide what it can create, per audience type. The source is
 * the effective permissions this app already fetches and caches (GET /api/permissions/effective
 * in {@link AuthPermissionsService}) — the library never calls the gateway itself. Aliased to
 * `DRIVE_CONVERSATION_PERMISSIONS` with `useExisting`.
 *
 * Re-read whenever the app refreshes its permissions (`AllowedOperationsUpdatedEvent`), and
 * cleared on logout so the next account never sees the previous one's rights.
 */
@Injectable({ providedIn: 'root' })
export class DriveConversationPermissionsService implements DriveConversationPermissions {
  private readonly _permissions: AuthPermissionsService = inject(AuthPermissionsService);

  // null (no entry yet, or none in the response) grants nothing, as PermissionService reads it.
  readonly coreConversation: WritableSignal<DriveEffectivePermissionEntry | null> =
    signal<DriveEffectivePermissionEntry | null>(this.read());

  constructor() {
    EmitterService.get(AllowedOperationsUpdatedEvent)
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.coreConversation.set(this.read()));
    EmitterService.get(UserLoggedOutEvent)
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.coreConversation.set(null));
  }

  private read(): DriveEffectivePermissionEntry | null {
    const entry: EffectivePermissionEntry | undefined =
      this._permissions.getEffectivePermissions()[FeatureDetail.CORE_CONVERSATION];
    return entry ? { access: entry.access, capabilities: entry.capabilities ?? null } : null;
  }
}
